import { Link } from "react-router-dom";
import { Heart, Users, Compass, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import { Seo } from "@/components/site/Seo";

const aboutImage =
  "https://images.unsplash.com/photo-1565728744382-61accd4aa148?auto=format&fit=crop&w=1400&q=80";

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Kernected Disability"
        description="Kernected Disability Support Services is an Australian NDIS Support Coordination provider built around the people we support. Person centred, family friendly, and independent."
        path="/about"
      />
      {/* HERO */}
      <section className="container-narrow py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 text-xs font-medium tracking-wide uppercase px-3 py-1.5 rounded-full">
              <span className="h-1.5 w-1.5 rounded-full bg-[#1B3A6B]" aria-hidden="true" />
              About Kernected · Brisbane
            </span>
            <h1 className="mt-6 font-display text-4xl md:text-5xl lg:text-[3.75rem] font-medium tracking-tight text-slate-900 leading-[1.05]">
              Built around the people we support
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-700 leading-relaxed">
              Kernected Disability Support Services is a Brisbane-based NDIS
              Support Coordination provider working with participants and
              families across Queensland and Australia. We navigate the NDIS
              with warmth, clarity, and quiet persistence.
            </p>
            <p className="mt-5 text-base md:text-lg text-slate-700 leading-relaxed">
              Our approach is simple: listen first, explain clearly, and connect
              you with the right supports. We treat every plan as personal,
              because it is.
            </p>
          </div>
          <div className="relative">
            <img
              src={aboutImage}
              alt="Family walking together outdoors in dappled sunlight"
              className="w-full h-[420px] md:h-[520px] object-cover rounded-3xl shadow-sm"
              data-testid="about-hero-image"
            />
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-white border-y border-slate-200">
        <div className="container-narrow py-16 md:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-sm font-semibold tracking-[0.18em] uppercase text-[#1B3A6B]">
              Our mission
            </p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-snug">
              Helping good plans become great outcomes
            </h2>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed">
              We exist to make Support Coordination feel less like paperwork and
              more like progress. From the first conversation to ongoing
              capacity building, we keep your goals at the centre.
            </p>
          </div>
          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              {
                icon: Heart,
                title: "Person centred",
                copy: "Your goals, preferences and lived experience shape every decision.",
              },
              {
                icon: Users,
                title: "Family friendly",
                copy: "We support participants and the people who care about them.",
              },
              {
                icon: Compass,
                title: "Clear guidance",
                copy: "Plain English explanations so you always know what's next.",
              },
              {
                icon: ShieldCheck,
                title: "Independent",
                copy: "We don't sell supports. We connect you with the right ones.",
              },
            ].map(({ icon: Icon, title, copy }) => (
              <li
                key={title}
                className="bg-[#FDFBF7] rounded-2xl border border-slate-200 p-7"
              >
                <Icon className="h-6 w-6 text-[#2A6496]" aria-hidden="true" />
                <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">
                  {title}
                </h3>
                <p className="mt-2 text-[15px] text-slate-700 leading-relaxed">
                  {copy}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* VALUES */}
      <section className="container-narrow py-16 md:py-24">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold tracking-[0.18em] uppercase text-[#1B3A6B]">
            Our values
          </p>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-snug">
            What you can expect from us
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            {
              title: "Respect",
              copy: "We honour your time, your story, and your right to make decisions about your own life.",
            },
            {
              title: "Reliability",
              copy: "We follow through. Calls returned, emails answered, and supports kept on track.",
            },
            {
              title: "Real outcomes",
              copy: "Coordination is only useful when it changes daily life for the better.",
            },
          ].map((v) => (
            <article
              key={v.title}
              className="bg-white rounded-2xl border border-slate-200/70 p-8"
            >
              <Sparkles className="h-6 w-6 text-[#1B3A6B]" aria-hidden="true" />
              <h3 className="mt-4 font-display text-2xl font-semibold text-slate-900">
                {v.title}
              </h3>
              <p className="mt-3 text-[15px] text-slate-700 leading-relaxed">
                {v.copy}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-narrow pb-24">
        <div className="bg-[#1B3A6B] text-white rounded-3xl p-10 md:p-14 grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <h2 className="font-display text-3xl md:text-4xl font-semibold leading-snug">
              Let's see if we're the right fit
            </h2>
            <p className="mt-3 text-lg text-slate-100/90">
              A short conversation is the best place to start. No pressure, no jargon.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="justify-self-start lg:justify-self-end inline-flex items-center gap-2 bg-white text-[#1B3A6B] hover:bg-[#F0F4F8] px-6 py-3.5 rounded-lg font-medium"
          >
            Contact our team <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
